import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ExternalLink, RefreshCw, ShieldAlert, CheckCircle2 } from "lucide-react";
import { endpoints } from "@/lib/api";

/**
 * Live ServiceNow side panel.
 * Fetches the actual record from the configured PDI (sc_request / incident)
 * and renders the key fields — state, priority, approval, opened_at, work notes.
 *
 * Props:
 *   open (bool)            — sheet visibility
 *   onOpenChange(bool)     — sheet open handler
 *   table (string)         — "sc_request" | "incident"
 *   sysId (string)         — ServiceNow sys_id of the record
 *   number (string)        — ServiceNow record number (REQ.../INC...) for header display
 *   fallbackReq (string)   — Flexspace REQ number to show when ServiceNow is disabled
 */
export default function ServiceNowPanel({ open, onOpenChange, table, sysId, number, fallbackReq }) {
  const [snStatus, setSnStatus] = useState(null);
  const [record, setRecord] = useState(null);
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchAll = async () => {
    setLoading(true);
    setError("");
    try {
      const st = await endpoints.snStatus();
      setSnStatus(st);
      if (st.enabled && sysId) {
        const r = await endpoints.snRecord(table || "sc_request", sysId);
        setRecord(r.record);
        setUrl(r.url);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to reach ServiceNow");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchAll();
  }, [open, table, sysId]); // eslint-disable-line react-hooks/exhaustive-deps

  const isMock = !snStatus?.enabled || !sysId;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        data-testid="servicenow-panel"
        className="w-full sm:max-w-lg bg-white p-0 overflow-y-auto"
      >
        {/* ServiceNow-styled header */}
        <div className="bg-[#293e40] text-white p-5 border-b-4 border-[#62d84e]">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#a8b3b4] font-semibold">
                <span className="w-1.5 h-1.5 rounded-full bg-[#62d84e]" />
                ServiceNow · {table === "incident" ? "Incident" : "Service Request"}
              </div>
              <SheetTitle className="text-white text-xl font-bold mt-1 font-mono">
                {number || record?.number || fallbackReq || "—"}
              </SheetTitle>
              <SheetDescription className="text-[#a8b3b4] text-xs mt-1">
                {isMock
                  ? "Preview — connect a ServiceNow PDI to see the live record here"
                  : "Live record fetched from your ServiceNow instance"}
              </SheetDescription>
            </div>
            <button
              data-testid="sn-panel-refresh"
              onClick={fetchAll}
              className="text-[#a8b3b4] hover:text-white p-1"
              title="Refresh"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        <SheetHeader className="sr-only">
          <SheetTitle>ServiceNow record</SheetTitle>
        </SheetHeader>

        {/* Body */}
        <div className="p-5 space-y-4">
          {isMock && (
            <div className="border border-amber-200 bg-amber-50 rounded-md p-3">
              <div className="flex items-start gap-2">
                <ShieldAlert size={14} className="text-amber-600 shrink-0 mt-0.5" />
                <div className="text-[12px] text-amber-900 leading-relaxed">
                  <div className="font-semibold text-amber-900">ServiceNow not yet connected</div>
                  <div className="mt-1 text-amber-800">
                    This preview shows the local Flexspace REQ. Wire a{" "}
                    <a
                      href="https://developer.servicenow.com"
                      target="_blank"
                      rel="noreferrer"
                      className="underline"
                    >
                      free PDI
                    </a>{" "}
                    (sign up · request instance · paste URL + admin creds into <code className="bg-amber-100 px-1 rounded text-[11px]">backend/.env</code> as{" "}
                    <code className="bg-amber-100 px-1 rounded text-[11px]">SN_INSTANCE_URL</code> /{" "}
                    <code className="bg-amber-100 px-1 rounded text-[11px]">SN_USERNAME</code> /{" "}
                    <code className="bg-amber-100 px-1 rounded text-[11px]">SN_PASSWORD</code>) and every future
                    order, incident, app install and model deployment will land in real ServiceNow — visible here as the live record.
                  </div>
                </div>
              </div>
            </div>
          )}

          {loading && (
            <div className="space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          )}

          {error && (
            <div className="border border-rose-200 bg-rose-50 rounded-md p-3 text-[12px] text-rose-800">
              {error}
            </div>
          )}

          {!isMock && record && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <SnField label="Number" value={record.number} mono />
                <SnField label="State" value={record.state}>
                  <StateBadge state={record.state} />
                </SnField>
                <SnField label="Priority" value={record.priority} />
                <SnField label="Urgency" value={record.urgency} />
                <SnField label="Opened" value={record.opened_at} />
                <SnField label="Updated" value={record.sys_updated_on} />
                {record.approval && <SnField label="Approval" value={record.approval} />}
                {record.assignment_group && <SnField label="Assignment group" value={record.assignment_group?.display_value || record.assignment_group} />}
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">
                  Short description
                </div>
                <div className="text-sm text-slate-900 font-semibold">
                  {record.short_description || "—"}
                </div>
              </div>

              {record.description && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">
                    Description
                  </div>
                  <pre
                    data-testid="sn-panel-description"
                    className="text-[12px] text-slate-700 whitespace-pre-wrap bg-slate-50 border border-slate-200 rounded p-3 leading-relaxed font-sans"
                  >
                    {record.description}
                  </pre>
                </div>
              )}

              {(record.comments || record.work_notes) && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">
                    Comments / Work notes
                  </div>
                  <pre className="text-[12px] text-slate-700 whitespace-pre-wrap bg-slate-50 border border-slate-200 rounded p-3 leading-relaxed font-sans">
                    {record.comments || record.work_notes || "—"}
                  </pre>
                </div>
              )}

              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">
                  sys_id
                </div>
                <div className="font-mono text-[11px] text-slate-500 break-all">{record.sys_id}</div>
              </div>

              {url && (
                <a
                  data-testid="sn-panel-open-record"
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex"
                >
                  <Button className="bg-[#293e40] hover:bg-[#1a2828] text-white w-full">
                    Open in ServiceNow
                    <ExternalLink size={13} className="ml-1.5" />
                  </Button>
                </a>
              )}
            </>
          )}

          {snStatus?.enabled && (
            <div className="pt-2 border-t border-slate-100 text-[11px] text-slate-500 flex items-center gap-1.5">
              <CheckCircle2 size={12} className="text-[#62d84e]" />
              Connected to <span className="font-mono">{snStatus.instance_url}</span>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function SnField({ label, value, mono, children }) {
  return (
    <div className="border border-slate-200 rounded-md p-2.5 bg-slate-50/50">
      <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      {children ? (
        <div className="mt-1">{children}</div>
      ) : (
        <div className={`text-sm text-slate-900 mt-0.5 truncate ${mono ? "font-mono" : ""}`}>
          {value || "—"}
        </div>
      )}
    </div>
  );
}

function StateBadge({ state }) {
  const s = (state || "").toString().toLowerCase();
  let color = "bg-slate-100 text-slate-700 border-slate-200";
  if (s.includes("open") || s.includes("1")) color = "bg-blue-50 text-blue-700 border-blue-200";
  if (s.includes("progress") || s.includes("2")) color = "bg-amber-50 text-amber-800 border-amber-200";
  if (s.includes("complete") || s.includes("resolved") || s.includes("3")) color = "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (s.includes("incomplete") || s.includes("cancelled") || s.includes("4")) color = "bg-rose-50 text-rose-700 border-rose-200";
  return <Badge variant="outline" className={`text-[10px] ${color}`}>{state || "—"}</Badge>;
}
